import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FieldValues, UseFormSetValue } from 'react-hook-form';
import type { FormatOptionLabelMeta } from 'react-select';

import {
  CUSTOMER_SEARCH_DEBOUNCE_MS,
  CUSTOMER_SEARCH_MIN_LENGTH,
} from 'constants/customerSelectSearch';

import { Option } from 'types';
import type { CustomerAddressDto } from 'types/api/customer.type';

import { useGetCustomerAddressesQuery, useGetCustomerByCodeQuery } from 'redux/api/ordersApi';

import { customersDataService } from 'services';

import { formatAddressSelectOptionLabel } from 'helpers/formatAddressDisplay';

/** Form field names the picker writes; the code and name selects of a block hold the same code. */
export const ORDER_ADDRESS_FIELDS = {
  customer: 'customer',
  customerName: 'customerName',
  shipping: 'shippingAddressCode',
  shippingName: 'shippingAddressName',
  goods: 'goodsDestinationCode',
  goodsName: 'goodsDestinationName',
} as const;

type PickedCustomer = { code: string; name: string };

/** Addresses picked in the form, for the create request (empty ones are left out). */
export const pickedAddresses = (
  data: Record<string, unknown>
): { shippingAddressCode?: string; goodsDestinationCode?: string } => {
  const shipping = data[ORDER_ADDRESS_FIELDS.shipping];
  const goods = data[ORDER_ADDRESS_FIELDS.goods];
  return {
    ...(typeof shipping === 'string' && shipping ? { shippingAddressCode: shipping } : {}),
    ...(typeof goods === 'string' && goods ? { goodsDestinationCode: goods } : {}),
  };
};

/**
 * Addresses shown in the form, for the duplicate request: both always sent, an empty block as
 * `''` (stored as none), so the copy gets exactly what the modal shows.
 */
export const shownAddresses = (
  data: Record<string, unknown>
): { shippingAddressCode: string; goodsDestinationCode: string } => {
  const code = (value: unknown) => (typeof value === 'string' ? value : '');
  return {
    shippingAddressCode: code(data[ORDER_ADDRESS_FIELDS.shipping]),
    goodsDestinationCode: code(data[ORDER_ADDRESS_FIELDS.goods]),
  };
};

/** Addresses of the order being copied, shown until another customer is picked. */
export type InitialAddresses = {
  shippingAddressCode?: string | null;
  goodsDestinationCode?: string | null;
};

type AddressKind = 'shipping' | 'goods';

/**
 * Name selects: the menu lists `name - code`, the selected value shows the name only
 * (kept in `additional.name`). Labels stay unique, so equal names are not merged.
 */
export const formatNameOptionLabel = (
  option: Option,
  { context }: FormatOptionLabelMeta<Option>
) => (context === 'value' ? option.additional?.name || option.label : option.label);

const nameOption = (code: string, name: string | null | undefined, extra?: string | null) => ({
  value: code,
  label: [name, code, extra].filter(Boolean).join(' - '),
  additional: { name: name || code },
});

const addressCodeOptions = (list: CustomerAddressDto[]): Option[] =>
  list.map((a) => ({ value: a.code, label: formatAddressSelectOptionLabel(a) }));

const addressNameOptions = (list: CustomerAddressDto[]): Option[] =>
  list.map((a) => nameOption(a.code, a.displayName, a.addressLine1));

type UseOrderAddressPickerParams = {
  zone: string;
  setValue: UseFormSetValue<FieldValues>;
  initialCustomer?: { code: string; name?: string } | null;
  initialAddresses?: InitialAddresses | null;
};

const useOrderAddressPicker = ({
  zone,
  setValue,
  initialCustomer,
  initialAddresses,
}: UseOrderAddressPickerParams) => {
  const [results, setResults] = useState<PickedCustomer[]>([]);
  // A search is waiting for its debounce or response ("no options" only after it ends).
  const [isSearching, setIsSearching] = useState(false);
  const [customer, setCustomer] = useState<PickedCustomer | null>(
    initialCustomer?.code
      ? { code: initialCustomer.code, name: initialCustomer.name ?? '' }
      : null
  );
  const [shippingCode, setShippingCode] = useState<string | null>(null);
  const [goodsCode, setGoodsCode] = useState<string | null>(null);
  // Blocks still waiting for the initial address (until it is applied, picked or the customer changes).
  const [prefillPending, setPrefillPending] = useState<Record<AddressKind, boolean>>({
    shipping: true,
    goods: true,
  });
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    },
    []
  );

  const setAddressFields = useCallback(
    (kind: AddressKind, code: string) => {
      const [codeField, nameField] =
        kind === 'shipping'
          ? [ORDER_ADDRESS_FIELDS.shipping, ORDER_ADDRESS_FIELDS.shippingName]
          : [ORDER_ADDRESS_FIELDS.goods, ORDER_ADDRESS_FIELDS.goodsName];
      setValue(codeField, code);
      setValue(nameField, code);
    },
    [setValue]
  );

  const pickCustomer = useCallback(
    (next: PickedCustomer | null, value: Option['value'] = next?.code ?? '') => {
      setCustomer(next);
      // Both selects hold the option value as the select gave it.
      setValue(ORDER_ADDRESS_FIELDS.customer, value);
      setValue(ORDER_ADDRESS_FIELDS.customerName, value);
      // Addresses belong to the customer: a new customer starts without them.
      setPrefillPending({ shipping: false, goods: false });
      setShippingCode(null);
      setGoodsCode(null);
      setAddressFields('shipping', '');
      setAddressFields('goods', '');
    },
    [setValue, setAddressFields]
  );

  // The name select shows the initial customer too (only on mount; later via pickCustomer).
  const initialCodeRef = useRef(initialCustomer?.code);
  useEffect(() => {
    if (initialCodeRef.current) {
      setValue(ORDER_ADDRESS_FIELDS.customerName, initialCodeRef.current);
    }
  }, [setValue]);

  // Customers are searched per zone: another zone drops the picked one.
  const zoneRef = useRef(zone);
  useEffect(() => {
    if (zoneRef.current === zone) return;
    zoneRef.current = zone;
    setResults([]);
    setIsSearching(false);
    pickCustomer(null);
  }, [zone, pickCustomer]);

  const searchCustomers = useCallback(
    (inputValue: string) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);

      if (!zone || inputValue.length < CUSTOMER_SEARCH_MIN_LENGTH) {
        setResults([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      const timer = setTimeout(() => {
        // Only the latest search updates the list and ends the loading state.
        const isLatest = () => debounceTimer.current === timer;
        // Only customers that can order (APPROVED, can_order), as on the order page.
        customersDataService
          .searchCustomers(inputValue, zone, undefined, undefined, undefined, true)
          .then((res) => {
            if (!isLatest()) return;
            setResults(
              (res.data.content ?? []).map((item) => ({
                code: item.customerCode,
                name: item.displayName,
              }))
            );
          })
          .catch(() => {
            if (isLatest()) setResults([]);
          })
          .finally(() => {
            if (isLatest()) setIsSearching(false);
          });
      }, CUSTOMER_SEARCH_DEBOUNCE_MS);
      debounceTimer.current = timer;
    },
    [zone]
  );

  // The picked customer stays selectable while new results come in.
  const customers = useMemo(() => {
    if (!customer || results.some((c) => c.code === customer.code)) return results;
    return [customer, ...results];
  }, [customer, results]);

  const customerCodeOptions: Option[] = useMemo(
    () => customers.map((c) => ({ value: c.code, label: `${c.code} - ${c.name}` })),
    [customers]
  );
  const customerNameOptions: Option[] = useMemo(
    () => customers.map((c) => nameOption(c.code, c.name)),
    [customers]
  );

  const selectCustomer = useCallback(
    (selected: Option) => {
      const code = selected?.value ? String(selected.value) : '';
      if (code === (customer?.code ?? '')) return;
      pickCustomer(
        code
          ? customers.find((c) => c.code === code) ?? { code, name: String(selected.label) }
          : null,
        code ? selected.value : ''
      );
    },
    [customer, customers, pickCustomer]
  );

  const { currentData: customerData, isError: isCustomerError } = useGetCustomerByCodeQuery(
    { customerCode: customer?.code ?? '' },
    { skip: !customer?.code }
  );
  const details = customerData?.details;
  const customerId = details?.customerId;

  const shippingQuery = useGetCustomerAddressesQuery(
    { customerId: customerId as number, type: 'SHIPPING' },
    { skip: !customerId }
  );
  const goodsQuery = useGetCustomerAddressesQuery(
    { customerId: customerId as number, type: 'GOODS_DESTINATION' },
    { skip: !customerId }
  );
  const shippingAddresses = shippingQuery.currentData ?? [];
  const goodsAddresses = goodsQuery.currentData ?? [];

  // Copy Order: show the source order's address once the customer's list is in
  // (left empty when the code is not in that list).
  const initialShipping = initialAddresses?.shippingAddressCode;
  const initialGoods = initialAddresses?.goodsDestinationCode;
  const shippingList = shippingQuery.currentData;
  const goodsList = goodsQuery.currentData;

  useEffect(() => {
    const blocks = [
      { kind: 'shipping', code: initialShipping, list: shippingList, show: setShippingCode },
      { kind: 'goods', code: initialGoods, list: goodsList, show: setGoodsCode },
    ] as const;
    blocks.forEach(({ kind, code, list, show }) => {
      if (!prefillPending[kind] || !code || !list) return;
      setPrefillPending((prev) => ({ ...prev, [kind]: false }));
      if (!list.some((a) => a.code === code)) return;
      show(code);
      setAddressFields(kind, code);
    });
  }, [
    prefillPending,
    initialShipping,
    initialGoods,
    shippingList,
    goodsList,
    setAddressFields,
  ]);

  /** True while an initial address is still loading (the form must not be sent yet). */
  const isPrefilling =
    ((prefillPending.shipping && Boolean(initialShipping)) ||
      (prefillPending.goods && Boolean(initialGoods))) &&
    !isCustomerError &&
    !shippingQuery.isError &&
    !goodsQuery.isError;

  const selectShipping = useCallback(
    (selected: Option) => {
      const code = selected?.value ? String(selected.value) : '';
      setPrefillPending((prev) => ({ ...prev, shipping: false }));
      setShippingCode(code || null);
      setAddressFields('shipping', code);
    },
    [setAddressFields]
  );
  const selectGoods = useCallback(
    (selected: Option) => {
      const code = selected?.value ? String(selected.value) : '';
      setPrefillPending((prev) => ({ ...prev, goods: false }));
      setGoodsCode(code || null);
      setAddressFields('goods', code);
    },
    [setAddressFields]
  );

  return {
    customerCodeOptions,
    customerNameOptions,
    searchCustomers,
    isSearching,
    selectCustomer,
    details,
    shippingCodeOptions: addressCodeOptions(shippingAddresses),
    shippingNameOptions: addressNameOptions(shippingAddresses),
    selectShipping,
    shippingAddress: shippingAddresses.find((a) => a.code === shippingCode) ?? null,
    goodsCodeOptions: addressCodeOptions(goodsAddresses),
    goodsNameOptions: addressNameOptions(goodsAddresses),
    selectGoods,
    goodsAddress: goodsAddresses.find((a) => a.code === goodsCode) ?? null,
    isPrefilling,
  };
};

export { useOrderAddressPicker };
